import axios from "axios";
import type { AxiosError, AxiosRequestConfig } from "axios";
import { getPublicApiOriginUrl } from "@/lib/config/publicRuntimeConfig";
import { nextLogger as logger } from "@/lib/utils/nextLogger";

// Get API URL - Development: IP-based, Production: Domain-based
export const getAPIURL = () => {
  // Browser-side detection - CHECK PRODUCTION FIRST (before environment variable)
  if (typeof window !== "undefined") {
    return getPublicApiOriginUrl();
  }

  // Server-side DEV: use local backend directly to avoid TLS/certificate issues on IP-based HTTPS proxy.
  if (process.env.NODE_ENV === "development") {
    return "http://127.0.0.1:3001";
  }

  return getPublicApiOriginUrl();
};

const apiClient = axios.create({
  baseURL: getAPIURL(),
  timeout: 30000, // 30 seconds timeout (increased for large file uploads and analysis)
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

export type RetryableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  skipRefresh?: boolean;
  skipAuthRedirect?: boolean;
  skipErrorLogging?: boolean;
};

export const BACKGROUND_AUTH_REDIRECT_POLICY: RetryableRequestConfig =
  Object.freeze({ skipAuthRedirect: true });
export const BACKGROUND_AUTH_REFRESH_POLICY: RetryableRequestConfig =
  Object.freeze({ skipRefresh: true });
export const BACKGROUND_AUTH_SILENT_POLICY: RetryableRequestConfig =
  Object.freeze({ skipRefresh: true, skipAuthRedirect: true });

type RefreshAccessTokenOptions = {
  emitSync?: boolean;
};

let refreshPromise: Promise<string | null> | null = null;
let refreshAbortController: AbortController | null = null;
let refreshBlockedUntil = 0;
let logoutInProgress = false;
const AUTH_SYNC_EVENT_KEY = "auth_sync_event";
const AUTH_CHANNEL_NAME = "auth_session";
const REFRESH_RETRY_BLOCK_MS = 30_000;

function readHeader(config: RetryableRequestConfig | undefined, key: string) {
  const headers = config?.headers as
    | Record<string, unknown>
    | { get?: (_name: string) => string | undefined }
    | undefined;
  if (!headers || typeof headers !== "object") return "";

  const maybeGet = (headers as { get?: (_name: string) => string | undefined })
    .get;
  if (typeof maybeGet === "function") {
    return (
      maybeGet.call(headers, key) ||
      maybeGet.call(headers, key.toLowerCase()) ||
      ""
    );
  }

  const value =
    (headers as Record<string, unknown>)[key] ??
    (headers as Record<string, unknown>)[key.toLowerCase()];
  return value == null ? "" : String(value);
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const entry = document.cookie
    .split("; ")
    .find((chunk) => chunk.startsWith(`${name}=`));
  if (!entry) return null;
  return decodeURIComponent(entry.split("=").slice(1).join("="));
}

function getCsrfToken(): string | null {
  return readCookie("csrf_token");
}

function emitAuthSync(type: "logout" | "auth_refreshed"): void {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify({ type, at: Date.now() });
  try {
    localStorage.setItem(AUTH_SYNC_EVENT_KEY, payload);
  } catch {
    // Ignore localStorage errors
  }
  try {
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.postMessage({ type, at: Date.now() });
    channel.close();
  } catch {
    // Ignore BroadcastChannel errors
  }
}

function shouldSkipRefresh(url?: string): boolean {
  const value = String(url || "");
  return (
    value.includes("/api/v1/auth/login") ||
    value.includes("/api/v1/auth/logout") ||
    value.includes("/api/v1/auth/refresh") ||
    value.includes("/api/v1/auth/register") ||
    value.includes("/api/v1/auth/forgot-password") ||
    value.includes("/api/v1/auth/reset-password") ||
    value.includes("/api/v1/auth/verify-email")
  );
}

function isRefreshBypassRequested(config?: RetryableRequestConfig): boolean {
  return Boolean(config?.skipRefresh);
}

function isAuthRedirectBypassRequested(
  config?: RetryableRequestConfig
): boolean {
  return Boolean(config?.skipAuthRedirect);
}

function isExpectedAuthFailure(error: AxiosError<{ code?: string }>): boolean {
  const code = String(error?.response?.data?.code || "");
  return (
    code === "missing_access_cookie" ||
    code === "missing_refresh_cookie" ||
    code === "invalid_access_token" ||
    code === "invalid_refresh_token" ||
    code === "expired_access_token" ||
    code === "expired_refresh_token" ||
    code === "user_cache_missing" ||
    code === "csrf_validation_failed" ||
    code === "refresh_replay_blocked" ||
    code === "refresh_race_retry" ||
    code === "refresh_in_progress_retry"
  );
}

function syncBrowserRuntimeBaseURL(config: RetryableRequestConfig): void {
  if (typeof window === "undefined") return;
  const runtimeBaseURL = getAPIURL();
  apiClient.defaults.baseURL = runtimeBaseURL;
  config.baseURL = runtimeBaseURL;
}

async function refreshAccessToken(
  options: RefreshAccessTokenOptions = {}
): Promise<string | null> {
  if (Date.now() < refreshBlockedUntil) {
    return null;
  }

  if (!refreshPromise) {
    refreshAbortController = new AbortController();
    const activeController = refreshAbortController;
    refreshPromise = apiClient
      .post<{ token?: string; accessToken?: string }>(
        "/api/v1/auth/refresh",
        {},
        {
          headers: {
            "X-Use-Cookie-Auth": "1",
            "X-Skip-Error-Logging": "1",
          },
          skipAuthRedirect: true,
          skipErrorLogging: true,
          signal: activeController.signal,
        } as RetryableRequestConfig
      )
      .catch(async (error) => {
        const code = error?.response?.data?.code;
        if (
          error?.response?.status === 409 &&
          (code === "refresh_in_progress_retry" ||
            code === "refresh_race_retry")
        ) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          return apiClient.post<{ token?: string; accessToken?: string }>(
            "/api/v1/auth/refresh",
            {},
            {
              headers: {
                "X-Use-Cookie-Auth": "1",
                "X-Skip-Error-Logging": "1",
              },
              skipAuthRedirect: true,
              skipErrorLogging: true,
              signal: activeController.signal,
            } as RetryableRequestConfig
          );
        }
        throw error;
      })
      .then((response) => {
        const nextToken = response.data?.accessToken || response.data?.token;
        const shouldEmitSync = options.emitSync !== false;
        if (typeof window !== "undefined" && nextToken && shouldEmitSync) {
          emitAuthSync("auth_refreshed");
        }
        return nextToken || null;
      })
      .catch(() => null)
      .finally(() => {
        refreshAbortController = null;
        refreshPromise = null;
      });
  }

  const token = await refreshPromise;
  if (!token) {
    refreshBlockedUntil = Date.now() + REFRESH_RETRY_BLOCK_MS;
  } else {
    refreshBlockedUntil = 0;
  }

  return token;
}

export async function refreshCookieAuthSession(
  options: RefreshAccessTokenOptions = {}
): Promise<string | null> {
  return refreshAccessToken(options);
}

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    syncBrowserRuntimeBaseURL(config as RetryableRequestConfig);

    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      config.headers = config.headers || {};
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }

    if (typeof window !== "undefined") {
      config.headers = config.headers || {};
      const method = String(config.method || "get").toUpperCase();
      const useCookieAuth =
        readHeader(config as RetryableRequestConfig, "X-Use-Cookie-Auth") ===
        "1";
      const csrfToken = getCsrfToken();

      if (useCookieAuth) {
        delete config.headers.Authorization;
        delete config.headers["X-Use-Cookie-Auth"];
        delete config.headers["x-use-cookie-auth"];
      }

      if (csrfToken && ["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        config.headers = config.headers || {};
        config.headers["X-CSRF-Token"] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function reportDevelopmentNetworkError(error: AxiosError): void {
  if (error.response || process.env.NODE_ENV !== "development") return;
  const config = error.config as RetryableRequestConfig | undefined;
  const url = config?.url || "";
  const skipByConfig = Boolean(config?.skipErrorLogging);
  const skipByHeader = readHeader(config, "X-Skip-Error-Logging") === "1";
  const isNonCriticalEndpoint = [
    "/reactions",
    "/responses",
    "/unread-count",
    "/notifications/unread",
    "/leaves/approver-status",
    "/onboarding/status",
  ].some((fragment) => url.includes(fragment));
  if (isNonCriticalEndpoint || skipByHeader || skipByConfig) return;
  logger.error("[API] Ag hatasi:", {
    message: error.message,
    code: error.code,
    url: config?.url,
    baseURL: config?.baseURL,
    method: config?.method,
  });
}

function reportDevelopmentResponseError(error: AxiosError): void {
  if (process.env.NODE_ENV !== "development") return;
  const config = error.config as RetryableRequestConfig | undefined;
  const skipByConfig = Boolean(config?.skipErrorLogging);
  const skipByHeader = readHeader(config, "X-Skip-Error-Logging") === "1";
  if (skipByHeader || skipByConfig) return;
  if (error.response?.status === 404) {
    logger.warn("[API] 404 hatasi:", {
      url: config?.url,
      method: config?.method,
      baseURL: config?.baseURL,
    });
  }
  if (Number(error.response?.status || 0) >= 500) {
    logger.debug("[API] Sunucu hatasi:", {
      status: error.response?.status,
      url: config?.url,
      data: error.response?.data,
    });
  }
}

function redirectToChatBridgeLogin() {
  window.location.href =
    "/api/v1/auth/google/start?flow=chat-bridge&returnTo=/chat-bridge";
}

function performAuthLogout() {
  if (logoutInProgress) return;
  logoutInProgress = true;
  localStorage.removeItem("auth_user");
  emitAuthSync("logout");
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
  setTimeout(() => {
    logoutInProgress = false;
  }, 1500);
}

function canRetryUnauthorized(
  originalRequest: RetryableRequestConfig,
  isChatBridgeRequest: boolean
): boolean {
  return (
    !isChatBridgeRequest &&
    !isRefreshBypassRequested(originalRequest) &&
    !originalRequest._retry &&
    !shouldSkipRefresh(originalRequest.url)
  );
}

async function recoverUnauthorizedRequest(
  error: AxiosError<{ code?: string }>
): Promise<unknown | null> {
  if (error.response?.status !== 401) return null;
  const originalRequest = (error.config || {}) as RetryableRequestConfig;
  const isChatBridgeRequest = String(originalRequest.url || "").startsWith(
    "/api/v1/chat-bridge"
  );
  const skipAuthRedirect = isAuthRedirectBypassRequested(originalRequest);
  const expectedAuthFailure = isExpectedAuthFailure(error);

  if (
    typeof window !== "undefined" &&
    canRetryUnauthorized(originalRequest, isChatBridgeRequest)
  ) {
    originalRequest._retry = true;
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) {
      originalRequest.headers = originalRequest.headers || {};
      delete originalRequest.headers.Authorization;
      return apiClient(originalRequest);
    }
  }

  if (
    typeof window !== "undefined" &&
    isChatBridgeRequest &&
    !skipAuthRedirect
  ) {
    redirectToChatBridgeLogin();
    return null;
  }

  if (
    typeof window !== "undefined" &&
    !skipAuthRedirect &&
    !expectedAuthFailure
  ) {
    performAuthLogout();
  }
  return null;
}

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Skip logging canceled requests (normal behavior during navigation/unmount)
    if (error.code === "ERR_CANCELED" || error.message === "canceled") {
      return Promise.reject(error);
    }

    reportDevelopmentNetworkError(error);

    const retryResponse = await recoverUnauthorizedRequest(error);
    if (retryResponse !== null) return retryResponse;

    // Handle 403 Forbidden - silently skip logging (expected RBAC behavior)
    // Some endpoints return 403 for non-admin users (e.g., /api/v1/departments for CALISAN)
    if (error.response?.status === 403) {
      // Suppress console error for expected 403s (RBAC-controlled endpoints)
      // The error is still rejected so components can handle it appropriately
      return Promise.reject(error);
    }

    reportDevelopmentResponseError(error);

    return Promise.reject(error);
  }
);

export async function requestCookieLogout(): Promise<void> {
  await apiClient.post("/api/v1/auth/logout", {}, {
    headers: {
      "X-Use-Cookie-Auth": "1",
      "X-Skip-Error-Logging": "1",
    },
    skipRefresh: true,
    skipAuthRedirect: true,
    skipErrorLogging: true,
  } as RetryableRequestConfig);
}

export function clearAuthNetworkState(): void {
  if (refreshAbortController) {
    refreshAbortController.abort();
    refreshAbortController = null;
  }
  refreshPromise = null;
  refreshBlockedUntil = Date.now() + REFRESH_RETRY_BLOCK_MS;
}

export function markAuthSessionEstablished(): void {
  if (refreshAbortController) {
    refreshAbortController.abort();
    refreshAbortController = null;
  }
  refreshPromise = null;
  refreshBlockedUntil = 0;
  logoutInProgress = false;
}

export function setLogoutInProgress(next: boolean): void {
  logoutInProgress = next;
  if (next) {
    clearAuthNetworkState();
  }
}

export default apiClient;
